import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Text, Button, VStack, Alert, AlertIcon } from '@chakra-ui/react'
import { FaLock, FaCreditCard } from 'react-icons/fa'
import { useAuthContext } from '../contexts/AuthContext'
import { usePaywallContext } from '../contexts/PaywallContext'
import { LoadingSpinner } from './LoadingSpinner'
import { PaywallPage } from './PaywallPage'
import { TrialCountdown } from './TrialCountdown'

interface PaywallGuardProps {
  children: React.ReactNode
  requireAuth?: boolean
  requireSubscription?: boolean
}

export const PaywallGuard: React.FC<PaywallGuardProps> = ({ 
  children, 
  requireAuth = true, 
  requireSubscription = true 
}) => {
  const navigate = useNavigate()
  const { isAuthenticated, loading: authLoading, user } = useAuthContext()
  const { 
    isPaywallEnabled, 
    isLoading: paywallLoading, 
    isSubscriptionActive, 
    isTrialExpired, 
    trialDaysRemaining,
    hasPaymentIssues,
    gracePeriodDays
  } = usePaywallContext()

  // Handle authentication redirects in useEffect
  useEffect(() => {
    if (!authLoading && !paywallLoading) {
      // If authentication is required but user is not authenticated
      if (requireAuth && !isAuthenticated) {
        navigate('/auth', { replace: true })
      }

      // If user is authenticated but email is not verified, redirect to verification
      if (isAuthenticated && user && !user.emailVerified) {
        const params = new URLSearchParams();
        params.set('email', user.email);
        navigate(`/verify-code?${params.toString()}`, { replace: true });
      }
    }
  }, [requireAuth, isAuthenticated, authLoading, paywallLoading, navigate, user])

  // Show loading while checking authentication and subscription status
  if (authLoading || paywallLoading) {
    return <LoadingSpinner />
  }

  // If authentication is required but user is not authenticated, show loading while redirecting
  if (requireAuth && !isAuthenticated) {
    return <LoadingSpinner />
  }

  // If user is authenticated but email is not verified, show loading while redirecting
  if (isAuthenticated && user && !user.emailVerified) {
    return <LoadingSpinner />
  }



  // If user is in trial period, show trial countdown overlay FIRST (before other checks)
  if (trialDaysRemaining !== null && trialDaysRemaining > 0) {
    return (
      <>
        {children}
        <TrialCountdown daysRemaining={trialDaysRemaining} />
      </>
    )
  }

  // If user is in grace period due to payment issues, show grace period warning
  if (hasPaymentIssues && gracePeriodDays !== null && gracePeriodDays > 0) {
    return (
      <>
        {children}
        <GracePeriodWarning daysRemaining={gracePeriodDays} />
      </>
    )
  }

  // If paywall is disabled, allow access
  if (!isPaywallEnabled) {
    return <>{children}</>
  }

  // If subscription is not required, allow access
  if (!requireSubscription) {
    return <>{children}</>
  }

  // If user is not authenticated and auth is not required, allow access
  if (!requireAuth && !isAuthenticated) {
    return <>{children}</>
  }

  // Check for new registrations who haven't completed their trial setup
  if (isAuthenticated && !isSubscriptionActive && trialDaysRemaining === null) {
    // Check if user has registration data in localStorage (indicating incomplete registration)
    const hasRegistrationData = localStorage.getItem('registrationPlanId') || 
                               localStorage.getItem('registrationPlanName') || 
                               localStorage.getItem('registrationOriginalAmount')
    
    if (hasRegistrationData) {
      // Redirect to paywall to complete registration
      navigate('/paywall?newRegistration=true', { replace: true })
      return <LoadingSpinner />
    }
  }

  // If user has active subscription, allow access
  if (isSubscriptionActive) {
    return <>{children}</>
  }

  // If trial has expired, show trial expiration modal
  if (isTrialExpired) {
    return (
      <>
        {children}
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="#09090b"
          zIndex={9999}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Box
            maxW="md"
            w="full"
            bg="#191919"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.700"
            p={8}
          >
            <VStack spacing={6} textAlign="center">
              <Box color="red.400" fontSize="4xl">
                <FaLock />
              </Box>
              
              <VStack spacing={3}>
                              <Text size="lg" color="white">
                Trial / Subscription Expired
              </Text>
              <Text color="gray.400">
                To continue using the scoring features, please subscribe to a plan.
              </Text>
              </VStack>

              <Alert
                status="info"
                borderRadius="md"
                bg="blue.900"
                border="1px solid"
                borderColor="blue.700"
              >
                <AlertIcon color="blue.400" />
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" fontWeight="medium" color="blue.100">
                    Subscription Required
                  </Text>
                  <Text fontSize="xs" color="blue.200">
                    Choose a plan to unlock unlimited scoring and all features.
                  </Text>
                </VStack>
              </Alert>

              <VStack spacing={3} w="full">
                <Button
                  leftIcon={<FaCreditCard />}
                  colorScheme="blue"
                  size="lg"
                  w="full"
                  onClick={() => navigate('/pricing')}
                >
                  View Plans & Subscribe
                </Button>
                <Button
                  variant="ghost"
                  color="gray.400"
                  w="full"
                  onClick={() => navigate('/')}
                >
                  Back to Home
                </Button>
              </VStack>
            </VStack>
          </Box>
        </Box>
      </>
    )
  }

  // Default case: show paywall
  return <PaywallPage />
}

// Grace Period Warning Component
interface GracePeriodWarningProps {
  daysRemaining: number
}

const GracePeriodWarning: React.FC<GracePeriodWarningProps> = ({ daysRemaining }) => {
  const navigate = useNavigate()

  return (
    <Box
      position="fixed"
      top={4}
      left="50%"
      transform="translateX(-50%)"
      zIndex={9999}
      maxW="400px"
      w="90%"
    >
      <Alert status="warning" borderRadius="md" boxShadow="lg">
        <AlertIcon />
        <VStack align="start" spacing={2} flex="1">
          <Text fontWeight="bold" fontSize="sm">
            Payment Issue Detected
          </Text>
          <Text fontSize="xs">
            Your subscription payment failed. You have {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining to update your payment method before access is restricted.
          </Text>
          <Button
            size="sm"
            colorScheme="orange"
            onClick={() => navigate('/profile?tab=payment')}
          >
            Update Payment Method
          </Button>
        </VStack>
      </Alert>
    </Box>
  )
} 